import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { MeResponse } from '../../hooks/useMe';
import styles from './SelfieConsentModal.module.css';

/** Version constant must match server BIOMETRIC_CONSENT_VERSION */
const BIOMETRIC_CONSENT_VERSION = 'v2';

/**
 * DB-backed predicate: returns true only when the user has current biometric consent.
 * Replaces the old localStorage-only hasSelfieConsent().
 */
export function hasCurrentBiometricConsent(me: MeResponse | undefined | null): boolean {
  if (!me) return false;
  return me.consentSelfieAt != null && me.consentSelfieVersion === BIOMETRIC_CONSENT_VERSION;
}

/**
 * @deprecated Use hasCurrentBiometricConsent(me) instead.
 * Kept as a shim so callers that haven't migrated don't crash at import time.
 * Always returns false — forces the consent modal to show until the DB-backed
 * check is wired in at the call site.
 */
export function hasSelfieConsent(): boolean {
  return false;
}

export interface SelfieConsentModalProps {
  open: boolean;
  /** JWT for the POST /api/v1/me/consent call */
  jwt: string | null;
  onAccept: () => void;
  onDecline: () => void;
  testId?: string;
}

export function SelfieConsentModal({
  open,
  jwt,
  onAccept,
  onDecline,
  testId = 'selfie-consent-modal',
}: SelfieConsentModalProps): JSX.Element | null {
  const { t } = useTranslation('flows');
  const [ageChecked, setAgeChecked] = useState(false);
  const [art9Checked, setArt9Checked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset checkboxes when the modal opens/closes
  useEffect(() => {
    if (!open) {
      setAgeChecked(false);
      setArt9Checked(false);
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onDecline();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onDecline]);

  if (!open) return null;

  const canAccept = ageChecked && art9Checked && !submitting;

  async function handleAccept(): Promise<void> {
    if (!canAccept) return;
    setSubmitting(true);
    setError(null);
    try {
      if (jwt) {
        const { apiFetch } = await import('../../lib/api-client');
        const res = await apiFetch('/api/v1/me/consent', jwt, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ type: 'biometric' }),
        });
        if (!res.ok) {
          setError(t('avatarFlow.consent.errorRetry'));
          setSubmitting(false);
          return;
        }
      }
      onAccept();
    } catch {
      setError(t('avatarFlow.consent.errorRetry'));
      setSubmitting(false);
    }
  }

  return (
    <div className={styles.overlay} role="dialog" aria-modal="true" data-testid={testId}>
      <div className={styles.sheet}>
        <h2 className={styles.title}>{t('avatarFlow.consent.title')}</h2>
        <p className={styles.body}>{t('avatarFlow.consent.subtitle')}</p>

        {/* Checkbox 1: 18+ age gate */}
        <label className={styles.checkRow} data-testid="consent-age-label">
          <input
            type="checkbox"
            checked={ageChecked}
            onChange={e => setAgeChecked(e.target.checked)}
            data-testid="consent-age-checkbox"
          />
          <span>{t('avatarFlow.consent.checkbox1')}</span>
        </label>

        {/* Checkbox 2: Art 9 biometric consent */}
        <label className={styles.checkRow} data-testid="consent-art9-label">
          <input
            type="checkbox"
            checked={art9Checked}
            onChange={e => setArt9Checked(e.target.checked)}
            data-testid="consent-art9-checkbox"
          />
          <span>
            {t('avatarFlow.consent.checkbox2')}{' '}
            <a
              href="https://calestea.com/privacy"
              target="_blank"
              rel="noreferrer"
              onClick={e => e.stopPropagation()}
            >
              {t('avatarFlow.consent.privacyLink')}
            </a>
          </span>
        </label>

        {error && (
          <p role="alert" style={{ color: 'var(--red, #e55)', fontSize: 13, margin: '8px 0 0' }}>
            {error}
          </p>
        )}

        <div className={styles.actions}>
          <button
            type="button"
            className={styles.btnPrimary}
            onClick={() => void handleAccept()}
            disabled={!canAccept}
            data-testid="selfie-consent-accept"
          >
            {submitting ? '…' : t('avatarFlow.consent.accept')}
          </button>
          <button
            type="button"
            className={styles.btnGhost}
            onClick={onDecline}
            data-testid="selfie-consent-decline"
          >
            {t('avatarFlow.consent.decline')}
          </button>
        </div>
      </div>
    </div>
  );
}
